import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Card, Col, Dropdown, Grid, message, Modal, Popconfirm, Row, Space} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    EllipsisOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import ProjectAgGridPanel from './ProjectAgGridPanel';
import ExcelUploadModal from '../ExcelUploadModal';
import {AG_GRID_DEFAULT_COL_DEF, formatDateTime} from '../../config/agGridConfig';
import SearchComponent from '../SearchComponent';
import {
    createProjectTrm,
    createProjectTrmBatch,
    deleteProjectTrmBatch,
    getProjectTrmList,
    safeUpsertProjectTrmBatch,
    updateProjectTrm,
    upsertProjectTrmBatch
} from '../../api/projectTrmApi';
import ProjectTrmCreateModal from './ProjectTrmCreateModal';
import useApiPermission from '../../hooks/useApiPermission';
import PermissionButton from '../common/PermissionButton';
import PermissionGate from '../common/PermissionGate';

// EditCellRenderer 제거 (툴바 선택수정 사용)

// props: { pjtId, columns, pageSize }
const ProjectTermsTab = ({ pjtId, columns, pageSize = 15 }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const gridRef = useRef();
  const screens = Grid.useBreakpoint();
  const isXs = !screens.md;

  // 권한 훅
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getProjectTrmList);
  const canCreate = canCall(createProjectTrm);
  const canUploadUpsert = canCall(upsertProjectTrmBatch);
  const canUploadCreate = canCall(createProjectTrmBatch);
  const canUpload = canUploadUpsert || canUploadCreate;
  const canDeleteBatch = canCall(deleteProjectTrmBatch);
  const [uploadStats, setUploadStats] = useState(null);
  const canUpdate = canCall(updateProjectTrm);

  const searchOptions = [
    { value: 'all', label: '전체' },
    { value: 'trmdNm', label: '용어명' },
    { value: 'trmExpln', label: '설명' },
    { value: 'trmEngAbbrNm', label: '영문약어' },
    { value: 'domainGroupNm', label: '도메인그룹' },
    { value: 'domainClsfNm', label: '도메인분류' },
    { value: 'domainNm', label: '도메인명' },
    { value: 'domainExpln', label: '도메인설명' },
    { value: 'dataType', label: '데이터유형' },
    { value: 'dataLen', label: '데이터길이' },
    { value: 'dcptLen', label: '소수점길이' },
    { value: 'strgFrm', label: '저장형식' },
    { value: 'indctFrm', label: '표시형식' },
    { value: 'unit', label: '단위' },
    { value: 'prmVl', label: '허용값' },
    { value: 'synm', label: '동의어' },
    { value: 'rgtr', label: '등록자' },
    { value: 'mdfr', label: '수정자' },
  ];

  // 작업 컬럼 제거한 기본 컬럼
  const baseColumns = useMemo(() => ([
     { headerName: '', field: 'checkbox', headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true, width: 50, pinned: 'left', lockPosition: true, suppressMenu: true, sortable: false, filter: false, resizable: false, suppressExport: true },
     { headerName: 'UUID', field: 'pjtTrmId', hide: true },
     { headerName: '도메인UUID', field: 'pjtDomainId', hide: true },
     { headerName: '용어명', field: 'trmdNm', minWidth: 180, flex: 1 },
     { headerName: '설명', field: 'trmExpln', minWidth: 240, flex: 1 },
     { headerName: '영문약어', field: 'trmEngAbbrNm', minWidth: 140 },
     { headerName: '도메인그룹', field: 'domainGroupNm', minWidth: 140 },
     { headerName: '도메인분류', field: 'domainClsfNm', minWidth: 140 },
     { headerName: '도메인명', field: 'domainNm', minWidth: 140 },
     { headerName: '도메인설명', field: 'domainExpln', minWidth: 200, flex: 1 },
     { headerName: '데이터유형', field: 'dataType', minWidth: 120 },
     { headerName: '데이터길이', field: 'dataLen', minWidth: 110 },
     { headerName: '소수점길이', field: 'dcptLen', minWidth: 120 },
     { headerName: '저장형식', field: 'strgFrm', minWidth: 120 },
     { headerName: '표시형식', field: 'indctFrm', minWidth: 120 },
     { headerName: '단위', field: 'unit', minWidth: 100 },
     { headerName: '허용값', field: 'prmVl', minWidth: 140 },
     { headerName: '동의어', field: 'synm', minWidth: 140 },
     { headerName: '등록자', field: 'rgtr', minWidth: 100 },
     { headerName: '등록일시', field: 'regDt', minWidth: 140, valueFormatter: (p) => formatDateTime(p.value) },
     { headerName: '수정자', field: 'mdfr', minWidth: 100 },
     { headerName: '수정일시', field: 'mdfcnDt', minWidth: 140, valueFormatter: (p) => formatDateTime(p.value) },
  ]), []);

  // 전달된 columns가 있어도 UUID 숨김과 체크박스 suppressExport 강제 적용 + 작업 컬럼 유지
  const effectiveColumns = useMemo(() => {
    const base = (columns || baseColumns).map((col) => {
      if (col.field === 'checkbox') return { ...col, suppressExport: true };
      if (col.field === 'pjtTrmId') return { headerName: col.headerName || 'UUID', ...col, hide: true };
      if (col.field === 'pjtDomainId') return { headerName: col.headerName || '도메인UUID', ...col, hide: true };
      return col;
    });
    const hasTrmUuid = base.some((c) => c.field === 'pjtTrmId');
    const hasDomUuid = base.some((c) => c.field === 'pjtDomainId');
    const ensure = [];
    if (!hasTrmUuid) ensure.push({ headerName: 'UUID', field: 'pjtTrmId', hide: true });
    if (!hasDomUuid) ensure.push({ headerName: '도메인UUID', field: 'pjtDomainId', hide: true });
    return ensure.length ? [...ensure, ...base] : base;
  }, [columns, baseColumns]);

  const fetchList = useCallback(async () => {
    if (!pjtId) return;
    setLoading(true);
    setError('');
    try {
      if (permLoading) return;
      if (!canRead) { setRows([]); setError('목록 조회 권한이 없습니다.'); return; }
      const res = await getProjectTrmList(pjtId);
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
    } catch (e) {
      setError('프로젝트 용어 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [pjtId, permLoading, canRead]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const getFilteredData = () => {
    if (!searchText.trim()) return rows;
    const keyword = searchText.toLowerCase();
    const match = (val) => val !== undefined && val !== null && String(val).toLowerCase().includes(keyword);
    return rows.filter((item) => {
      if (searchColumn === 'all') {
        return searchOptions.filter((o) => o.value !== 'all').some((o) => match(item[o.value]));
      }
      return match(item[searchColumn]);
    });
  };

  const deleteSelected = async () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (!selectedNodes.length) { message.warning('삭제할 항목을 선택하세요.'); return; }
    const idList = selectedNodes.map((n) => n.data?.pjtTrmId).filter(Boolean);
    if (!idList.length) { message.warning('유효한 항목이 없습니다.'); return; }
    try {
      await deleteProjectTrmBatch(pjtId, idList);
      message.success(`${idList.length}개 항목을 삭제했습니다.`);
      await fetchList();
      gridRef.current.api.deselectAll();
    } catch (e) {
      // 에러 토스트는 공통 인터셉터에서 처리
    }
  };

  const openEdit = useCallback((row) => {
    if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
    setEditItem(row);
    setIsEditModalOpen(true);
  }, [permLoading, canUpdate]);

  const handleEditSelected = useCallback(() => {
    if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selected = gridRef.current.api.getSelectedNodes();
    if (selected.length !== 1) { message.warning('수정할 항목 1건을 선택하세요.'); return; }
    openEdit(selected[0].data);
  }, [permLoading, canUpdate, openEdit]);

  // SheetJS를 사용한 XLSX 내보내기: 첫 열 UUID, 그리드 표시 순서와 일치
  const handleDownloadExcel = () => {
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }

    // 표시 중인 컬럼(재정렬 반영)을 수집, suppressExport/UUID 제외
    const displayed = gridRef.current.columnApi
      ? gridRef.current.columnApi.getAllDisplayedColumns().map((col) => {
          const def = col.getColDef();
          return { field: def.field, headerName: def.headerName || def.field, suppressExport: !!def.suppressExport };
        }).filter((c) => c.field && !c.suppressExport && c.field !== 'pjtTrmId' && c.field !== 'pjtDomainId')
      : effectiveColumns.filter((c) => c.field && !c.suppressExport && c.field !== 'pjtTrmId' && c.field !== 'pjtDomainId').map((c) => ({ field: c.field, headerName: c.headerName || c.field }));

    const header = ['UUID', '도메인UUID', ...displayed.map((c) => c.headerName)];
    const rowsAoA = [header];

    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      const dataRow = [node.data?.pjtTrmId ?? '', node.data?.pjtDomainId ?? ''];
      displayed.forEach((c) => {
        let val = node.data?.[c.field];
        if (c.field === 'regDt' || c.field === 'mdfcnDt') val = formatDateTime(val);
        dataRow.push(val ?? '');
      });
      rowsAoA.push(dataRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(rowsAoA);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Terms');
    XLSX.writeFile(wb, `project-terms_${pjtId}.xlsx`);
  };

  // 엑셀 업로드 가이드/컬럼 정의 (업로드용 템플릿)
  const excelColumnDefs = [
    { headerName: 'UUID', field: 'pjtTrmId', editable: false, minWidth: 240 },
    { headerName: '도메인UUID', field: 'pjtDomainId', editable: false, minWidth: 240 },
    { headerName: '용어명*', field: 'trmdNm', editable: true, minWidth: 180 },
    { headerName: '설명*', field: 'trmExpln', editable: true, minWidth: 240, flex: 1 },
    { headerName: '영문약어', field: 'trmEngAbbrNm', editable: true, minWidth: 160 },
    { headerName: '도메인명', field: 'domainNm', editable: true, minWidth: 160 },
    { headerName: '동의어', field: 'synm', editable: true, minWidth: 140 },
  ];
  const excelFieldMapping = ['pjtTrmId', 'pjtDomainId', 'trmdNm', 'trmExpln', 'trmEngAbbrNm', 'domainNm', 'synm'];
  const excelGuide = 'A: UUID | B: 도메인UUID | C: *용어명 | D: *설명 | E: 영문약어 | F: 도메인명 | G: 동의어';

  // 작은 화면 드롭다운에서 사용할 삭제 확인 모달
  const confirmDeleteFromMenu = () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (!selectedNodes.length) { message.warning('삭제할 항목을 선택하세요.'); return; }
    Modal.confirm({ title: '선택 항목 삭제', content: '선택된 항목을 삭제하시겠습니까?', okText: '삭제', okButtonProps: { danger: true }, cancelText: '취소', onOk: deleteSelected });
  };

  const actionMenuItems = [
    ...(canCreate ? [{ key: 'create', icon: <PlusOutlined />, label: '신규생성' }] : []),
    ...(canUpdate ? [{ key: 'edit', icon: <EditOutlined />, label: '선택 항목 수정' }] : []),
    ...(canDeleteBatch ? [{ type: 'divider' }, { key: 'delete', icon: <DeleteOutlined />, label: '선택 항목 삭제', danger: true }] : []),
    ...(canUpload ? [{ type: 'divider' }, { key: 'upload', icon: <UploadOutlined />, label: canUploadUpsert ? '엑셀 업로드' : '엑셀 업로드' }] : []),
    { key: 'download', icon: <DownloadOutlined />, label: '엑셀 다운로드' },
    ...(canRead ? [{ type: 'divider' }, { key: 'refresh', icon: <ReloadOutlined />, label: '새로고침' }] : []),
  ];

  const onActionMenuClick = ({ key }) => {
    switch (key) {
      case 'create': (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true); break;
      case 'edit': handleEditSelected(); break;
      case 'delete': confirmDeleteFromMenu(); break;
      case 'upload': (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true); break;
      case 'download': handleDownloadExcel(); break;
      case 'refresh': (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList(); break;
      default: break;
    }
  };

  const getContextMenuItems = React.useCallback((params) => {
     const items = [];
     if (params?.node?.data) {
       items.push({ name: '수정', action: () => openEdit(params.node.data) });
       items.push('separator');
     }
     items.push('copy');
     items.push('separator');
     items.push('export');
     return items;
  }, [openEdit]);

  return (
    <PermissionGate require={getProjectTrmList}>
      {({allowed}) => (
        <div style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24}>
                {isXs ? (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SearchComponent searchColumn={searchColumn} setSearchColumn={setSearchColumn} searchText={searchText} setSearchText={setSearchText} searchOptions={searchOptions} defaultSearchColumn={'trmdNm'} />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Dropdown menu={{ items: actionMenuItems, onClick: onActionMenuClick }} placement="bottomRight" trigger={["click"]}>
                        <Button aria-label="작업 메뉴" icon={<EllipsisOutlined />} />
                      </Dropdown>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                      <SearchComponent searchColumn={searchColumn} setSearchColumn={setSearchColumn} searchText={searchText} setSearchText={setSearchText} searchOptions={searchOptions} defaultSearchColumn={'trmdNm'} />
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                      <Space wrap>
                        <PermissionButton type="primary" icon={<PlusOutlined />} onClick={() => (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true)} disabled={permLoading || !canCreate} require={createProjectTrm}>신규생성</PermissionButton>
                        <PermissionButton icon={<EditOutlined />} type="default" onClick={handleEditSelected} disabled={permLoading || !canUpdate} require={updateProjectTrm}>선택수정</PermissionButton>
                        <Popconfirm title="선택 항목 삭제" description="선택된 항목을 삭제하시겠습니까?" onConfirm={deleteSelected} okText="삭제" cancelText="취소" disabled={permLoading || !canDeleteBatch}>
                          <PermissionButton danger icon={<DeleteOutlined />} disabled={permLoading || !canDeleteBatch} require={deleteProjectTrmBatch}>선택 항목 삭제</PermissionButton>
                        </Popconfirm>
                        <PermissionButton type="primary" icon={<UploadOutlined />} onClick={() => (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true)} disabled={permLoading || !canUpload} require={canUploadUpsert ? upsertProjectTrmBatch : createProjectTrmBatch} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>{canUploadUpsert ? '엑셀 업로드(업서트)' : '엑셀 업로드'}</PermissionButton>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadExcel} style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}>엑셀 다운로드</Button>
                        <PermissionButton icon={<ReloadOutlined />} onClick={() => (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList()} disabled={permLoading || !canRead} require={getProjectTrmList}>새로고침</PermissionButton>
                      </Space>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Card>

          <ProjectAgGridPanel gridRef={gridRef} rowData={getFilteredData()} columnDefs={effectiveColumns} pageSize={pageSize} loading={loading} error={error} defaultColDef={AG_GRID_DEFAULT_COL_DEF}
            onRowDoubleClicked={(e) => e?.data && openEdit(e.data)}
            getContextMenuItems={getContextMenuItems}
          />

          <ProjectTrmCreateModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={fetchList} pjtId={pjtId} />
          <ProjectTrmCreateModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSuccess={async () => { await fetchList(); setEditItem(null); }} pjtId={pjtId} mode="edit" initialValues={editItem} />
          <ExcelUploadModal
            isOpen={isExcelModalOpen}
            onClose={() => setIsExcelModalOpen(false)}
            onSuccess={async () => { await fetchList(); }}
            agGridColumnDefs={excelColumnDefs}
            agGridDefaultColDef={AG_GRID_DEFAULT_COL_DEF}
            fieldMapping={excelFieldMapping}
            uploadApi={async (rows) => {
              const normalized = rows.map(r => {
                const trim = v => (typeof v === 'string' ? v.trim() : v);
                const nz = v => { const t = trim(v); return t === '' ? null : t; };
                const obj = {
                  pjtTrmId: nz(r.pjtTrmId),
                  pjtDomainId: nz(r.pjtDomainId),
                  trmdNm: trim(r.trmdNm) || '',
                  trmExpln: trim(r.trmExpln) || '',
                  trmEngAbbrNm: nz(r.trmEngAbbrNm),
                  domainNm: nz(r.domainNm),
                  synm: nz(r.synm),
                };
                if (!canUploadUpsert) { obj.pjtTrmId = undefined; }
                return obj;
              });
              const result = await safeUpsertProjectTrmBatch(pjtId, normalized, { forceClient: !canUploadUpsert });
              setUploadStats(result);
            }}
            excelGuide={excelGuide + (canUploadUpsert ? '\n* 업서트: UUID(pjtTrmId) 동일 시 UPDATE, 없으면 INSERT' : '\n* 업서트 권한 없음: UUID 무시하고 모두 신규 생성')}
            rowKey="rowKey"
          />

          {uploadStats && (
            <div style={{ marginTop: 12 }}>
              <Card size="small" style={{ background:'#fafafa' }}>
                업로드 결과: serverUpsert={String(uploadStats.serverUsed)} | updated={uploadStats.updated} | created={uploadStats.created} | errors={uploadStats.errors.length}
              </Card>
            </div>
          )}
        </div>
      )}
    </PermissionGate>
  );
};

export default ProjectTermsTab;

